import {onMounted, onUnmounted, ref} from "vue";
import { getCSSVars } from '@/config/theme.js';
import { branding } from '@/config/branding.js';

const LS_COLOR_SCHEME_KEY = 'DougJones:scheme';

function applyScheme(scheme, persist = false) {
  document.documentElement.setAttribute('scheme', scheme);
  document.documentElement.setAttribute('data-theme', branding.themePreset);
  if (scheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  if (persist) {
    localStorage.setItem(LS_COLOR_SCHEME_KEY, scheme);
  }

  const vars = getCSSVars(branding.themePreset, scheme);
  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });
}

function getSystemScheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getSavedScheme() {
  return localStorage.getItem(LS_COLOR_SCHEME_KEY);
}

function removeSavedScheme() {
  localStorage.removeItem(LS_COLOR_SCHEME_KEY);
}

function initSystemSchemeListener() {
  const matchMedia = window.matchMedia('(prefers-color-scheme: dark)');
  const updateScheme = () => {
    const savedScheme = getSavedScheme();
    if (!savedScheme) {
      applyScheme(getSystemScheme());
    }
  };

  matchMedia.addEventListener('change', updateScheme);
  updateScheme();

  return () => {
    matchMedia.removeEventListener('change', updateScheme);
  };
}

function useSchemeObserver() {
  const currentScheme = ref(document.documentElement.getAttribute('scheme') || 'dark');

  let observer;

  const startObserver = () => {
    observer = new MutationObserver(() => {
      currentScheme.value = document.documentElement.getAttribute('scheme') || 'dark';
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['scheme'],
    });
  };

  const stopObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  onMounted(startObserver);
  onUnmounted(stopObserver);

  return currentScheme;
}

export { applyScheme, getSystemScheme, getSavedScheme, removeSavedScheme, initSystemSchemeListener, useSchemeObserver };
